import { HashLoader } from "react-spinners";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BookingResponseData } from "../../../lib/@types";
import { useTrips } from "../../../lib/context/TripsContext";
import { useTripsIframeMessages } from "../hooks/useTripsIframeMessages";
import { appendTripsWidgetQuery } from "../utils/appendTripsWidgetQuery";

type Props = {
  /**
   * Same scope as "New local flight": domestic routes only. Rejects widget payloads
   * whose TripsType is present and not "domestic" (e.g. international).
   */
  domesticOnly?: boolean;
};

/**
 * Trips partner iframe flow (same init API as legacy NewFlight).
 */
const TripsBookingIframe = ({ domesticOnly = true }: Props) => {
  const { bookingURL, booking, setBooking, setBookingResponse } = useTrips();
  const [isLoading, setIsLoading] = useState(true);
  const [domesticRejected, setDomesticRejected] = useState(false);

  const iframeSrc = useMemo(
    () => (bookingURL ? appendTripsWidgetQuery(bookingURL) : null),
    [bookingURL]
  );

  useEffect(() => {
    if (!bookingURL) {
      setBooking(true);
    }
  }, [bookingURL, setBooking]);

  const onBookingPayload = useCallback(
    (data: BookingResponseData) => {
      if (domesticOnly && data.TripsType) {
        if (data.TripsType.trim().toLowerCase() !== "domestic") {
          setDomesticRejected(true);
          return;
        }
      }
      setDomesticRejected(false);
      setBooking(false);
      setBookingResponse(data);
    },
    [domesticOnly, setBooking, setBookingResponse]
  );

  useTripsIframeMessages({ onBookingPayload });

  return (
    <div className="h-screen w-screen flex flex-col">
      {domesticRejected && (
        <div
          className="shrink-0 mx-4 mt-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          role="alert"
        >
          This checkout is for <strong>local (domestic) flights</strong> only. Pick a
          domestic route in the search below, or use{" "}
          <span className="font-medium">International</span> from the aviation menu when
          available.
          <button
            type="button"
            className="ml-2 font-medium text-visaro-primary-700 underline"
            onClick={() => setDomesticRejected(false)}
          >
            Dismiss
          </button>
        </div>
      )}
      {(isLoading || !iframeSrc || !booking) && (
        <div className="flex-1 flex items-center justify-center min-h-[40vh]">
          <HashLoader />
        </div>
      )}
      {iframeSrc && booking && (
        <iframe
          src={iframeSrc}
          title="Local flight booking"
          style={{ width: "100%", height: "90vh", border: "none" }}
          onLoad={() => setIsLoading(false)}
        />
      )}
    </div>
  );
};

export default TripsBookingIframe;
